import * as React from 'react';
import { Input, InputNumber, Switch, Form, Layout, Button, Dropdown, Menu, notification, Col, Row, Typography, message, Popconfirm, FormInstance, Modal} from 'antd';
import GrowerDTO from 'src/models/GrowerDTO';
import EnterpriseApiService from 'src/api/EnterpriseApiService';
import { Link } from 'react-router-dom';
import Routes from 'src/consts/Routes';
import RoleUtil from 'src/utils/RoleUtil';
import Role from 'src/consts/Role';
import { Select }  from 'antd';
import { LabeledValue } from "antd/lib/select";
import { SearchOptionRender, usePremierUserSearchQuery } from '../users/ExternalUserEdit';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'antd/es/form/Form';
import { PlusOutlined } from '@ant-design/icons';
import { ProHelpSelect } from '@ant-design/pro-layout';
import Table, { ColumnProps } from 'antd/es/table';
import { useDebouncedCallback } from 'use-debounce';
import { DEBOUNCE_TIME_MS } from '../users/UserList';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getGrowerIdsOptions } from 'src/queries/getGrowerIdsOptions';
import { ApiError } from 'src/api/ApiResultHandler';

interface State {
    chosenGrowerId: number | null;
    chosenGrowerName: string;
    currentGrower: GrowerDTO | null;
    growerIdList: [number, string][];
}

const { Option } = Select;
const { Text } = Typography;

interface GrowerFormModalProps {
    open: boolean;
    selectedGrower: GrowerDTO;
    onClose: () => void;
    onDelete: (id: number) => void;
}

const updateGrowerMutationKey = ["updateGrower"] as const;
const useAddorUpdateOrganizationMutation = () => useMutation({
    mutationKey: updateGrowerMutationKey,
    mutationFn: async (formGrower: GrowerDTO) => {
        if (formGrower.growerID) {
            await EnterpriseApiService.updateGrower(formGrower);
        }
        else {
            await EnterpriseApiService.addGrower(formGrower);
        }
    }
})

const GrowerFormModal = (props: GrowerFormModalProps) => {
    const [form] = useForm<Partial<GrowerDTO>>();
    const queryClient = useQueryClient();
    const addorUpdatGrowerMutation = useAddorUpdateOrganizationMutation();
    const isMutating = queryClient.isMutating({ mutationKey: updateGrowerMutationKey });


    useEffect(() => {
        if (props.selectedGrower) {
            form.setFieldsValue({
                externalBinIds: props.selectedGrower.externalBinIds,
                externalId: props.selectedGrower.externalId,
                growerID: props.selectedGrower.growerID,
                isActive: props.selectedGrower.isActive,
                isExternal: props.selectedGrower.isExternal,
                notes: props.selectedGrower.notes,
                growerName: props.selectedGrower.growerName,

            });
        } else {
            form.resetFields();
        }
    }, [props.selectedGrower, form]);

    const handleSubmit = async (values: GrowerDTO) => {
        const formGrower = { ...values, growerID: props.selectedGrower?.growerID || 0 };
        try {
            if (props.selectedGrower?.growerID) {
                await addorUpdatGrowerMutation.mutateAsync(formGrower)
                notification.success({ message: 'Organization Updated Successfully' });
            } else {
                await addorUpdatGrowerMutation.mutateAsync(formGrower)
                notification.success({ message: 'New Organization Added Successfully' });
            }
            queryClient.invalidateQueries({queryKey: [getGrowerIdsOptions(false).queryKey[0]]});
            props.onClose();
        } catch (error) {
            notification.error({ message: error.message, description: error.description });
        }
    };

    return (
        <Modal open={props.open} title={props.selectedGrower?.growerID ? 'Edit Organization' : 'Create Organization'}
            onCancel={props.onClose} footer={null} width="fit-content">
            <Form form={form} labelWrap labelCol={{style: {width: '160px'}}} onFinish={handleSubmit} initialValues={props.selectedGrower || {}}>
                <Form.Item label="Organization Name" name="growerName" rules={[{ required: true, message: 'Enter the name of the Grower' }]}>
                    <Input />
                </Form.Item>
                <Form.Item name="isActive" label="Is Active">
                    <Switch />
                </Form.Item>
                <Form.Item label="Notes" name="notes">
                    <Input />
                </Form.Item>
                <Form.Item
                    label="Shivvers Bins"
                    name="externalBinIds"
                    >
                    <Search />
                </Form.Item>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {props.selectedGrower?.growerID > 0 && (
                        <Popconfirm title="Delete this Organization?" onConfirm={() => props.onDelete(props.selectedGrower.growerID)} okText="Delete">
                            <Button type="primary" danger>Delete</Button>
                        </Popconfirm>
                    )}
                    <Button type="primary" htmlType="submit" loading={isMutating > 0} disabled={isMutating > 0}>Submit</Button>
                </div>
            </Form>
        </Modal>
    );
};


const GrowerAdministration = ({ grower, isSettings }: {grower?: [number, string], isSettings: boolean}) => {
    const [chosenGrowerId, setChosenGrowerId] = useState<State['chosenGrowerId']>(grower ? grower[0] : null);
    const [currentGrower, setCurrentGrower] = useState<State['currentGrower']>(null);

    const queryClient = useQueryClient();
    const growerIdOptions_ = getGrowerIdsOptions(false);

    const growerIdListQuery = useQuery({...growerIdOptions_, enabled: RoleUtil.currentUserIsAdmin()})
    const growerIdList = growerIdListQuery.data ?? [];
    const [form] = useForm<GrowerDTO>();
    const [showModal, setShowModal] = useState(false);

        const [organizationSearch, setOrganizationSearch] = useState("");
        const organizationSearchDelayedValue = useDebouncedCallback((value) => {
            setOrganizationSearch(value);
        }, DEBOUNCE_TIME_MS);

    React.useEffect(() => {
        if (RoleUtil.currentUserIsAdmin()) {

        } else if (chosenGrowerId) {
            handleGrowerIdClicked(chosenGrowerId);
        }
    }, []);

    useEffect(() => {
        const error = growerIdListQuery.error as ApiError;
        if (error != null) {
            console.error("error during groweridlist query", error);
            notification.error({ message: error.message, description: error.description });
        }
    }, [growerIdListQuery.error])

    const handleGrowerIdClicked = async (id: number) => {
        if (id) {
            try {
                const grower = await EnterpriseApiService.getGrower(id);
                setChosenGrowerId(grower.growerID);
                setCurrentGrower(grower);
                form.resetFields();
            } catch (error) {
                console.error("error setting grower clicked", error);
                notification.error({ message: error.message, description: error.description });
            }
        } else {
            setChosenGrowerId(null);
            setCurrentGrower(GrowerDTO.create());
            form?.resetFields();
        }
    };

    const handleDelete = async () => {
        try {
            await EnterpriseApiService.removeGrower(chosenGrowerId!);
            message.success('Success!');
            setChosenGrowerId(null);
            setCurrentGrower(null);
            queryClient.invalidateQueries({queryKey: [growerIdOptions_.queryKey[0]]});
        } catch (error) {
            message.error('Deletion failed!');
        }
    };

    const columns: ColumnProps[] = [
        {
            hidden: true,
            dataIndex: 'id',
        },
        {
        title: "Organization Name",
        dataIndex: "growerName",
        key: 'name',
        width: "64ch",
        sorter: (a, b) => (a.growerName || '').localeCompare(b.growerName || ''),
        defaultSortOrder: 'ascend',
        render(value, record, index) {

            return <span style={{width: "100%"}} onClick={async () => {
                await handleGrowerIdClicked(record.growerID);
                setShowModal(true);
            }}>
                    <Typography.Link style={{userSelect: 'text'}}>{value}</Typography.Link>
                </span>
        },
    }];

    const tableRows = growerIdList;

    const updateFilters = () => {
        if (organizationSearch === "") {
            return tableRows ?? [];
        }

        const filteredTable = tableRows
            ?.filter((record) => record.growerName?.toUpperCase().includes(organizationSearch.toUpperCase()))
            ?? [];
        return filteredTable;
    };
    
    const filteredData = useMemo(() => updateFilters(), [organizationSearch, tableRows]);

    return (
        <Layout.Content className="GrowerAdministration">
            <Row gutter={[16, 16]}>
                {RoleUtil.currentUserIsAdmin() && <>
                    <Col>
                    <Button 
                        type="primary"
                        icon={<PlusOutlined />}
                        block
                        onClick={async () => {
                            await handleGrowerIdClicked(0);
                            setShowModal(true);
                        }}
                    >
                        Create new organization
                    </Button>
                    </Col>
                    <Col>
                        <Input style={{ width: 320 }} onChange={(evt) => organizationSearchDelayedValue(evt.target.value)} placeholder='Search Organizations' />
                        <Table tableLayout='auto' style={{width: "400px", paddingTop: 32}} size="small"  columns={columns} dataSource={filteredData} sticky pagination={false} scroll={{y: '55vh', x: "fit-content"}} />
                    </Col>
                </>}
            </Row>

            {currentGrower && (
                <GrowerFormModal open={showModal} selectedGrower={currentGrower} key={chosenGrowerId}
                 onDelete={handleDelete}
                  onClose={() => {
                    setShowModal(false);
                    setChosenGrowerId(null);
                }} />
            )}
        </Layout.Content>
    );
};

// requuirements for a custom form control https://ant.design/components/form#form-demo-customized-form-controls
interface SearchProps {
    id?: string;
    value?: number[];
    onChange?: (value: number[]) => void;
}
export const Search = (props: SearchProps) => {
    const query = usePremierUserSearchQuery({onlyCustomers: true});

    const options = query.data?.map(u => {
        return { ...u, value: u.externalId, label: `${u.firstName} ${u.lastName}` }
    }) ?? [];
    return <Select {...props} mode="multiple" placeholder="Search by last, first or account" showSearch filterOption={(input, option) => {
        return (option?.label?.toUpperCase().includes(input.toUpperCase())
            || option?.account?.toUpperCase().includes(input.toUpperCase())) ?? false;

    }} style={{ width: "400px" }} loading={query.isFetching} options={options} optionRender={(option) => {
        return <SearchOptionRender accountName={option.label} accountIp={option.data.account} />
    }} />
}

export default GrowerAdministration;
